import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import {
    LayoutDashboard, Ticket, BadgePercent, Store, Building2,
    Users, KeyRound, FileBarChart, LogOut, QrCode, Package, Mail
} from "lucide-react";

const navItems = [
    { to: "/admin", end: true, icon: LayoutDashboard, label: "Dashboard" },
    { to: "/admin/vouchers", icon: Ticket, label: "Vouchers" },
    { to: "/admin/coupons", icon: BadgePercent, label: "Coupons" },
    { to: "/admin/bundle-templates", icon: Package, label: "Bundle Templates" },
    { to: "/admin/email-templates", icon: Mail, label: "Email Templates" },
    { to: "/admin/branches", icon: Store, label: "Branches" },
    { to: "/admin/cost-shops", icon: Building2, label: "Cost Shops" },
    { to: "/admin/reports", icon: FileBarChart, label: "Reports" },
    { to: "/admin/users", icon: Users, label: "Admin Users", master: true },
    { to: "/admin/api-keys", icon: KeyRound, label: "API Keys", master: true },
];

export default function AdminLayout() {
    const { user, logout } = useAuth();
    const nav = useNavigate();

    const handleLogout = async () => {
        await logout();
        nav("/login");
    };

    return (
        <div className="min-h-screen bg-crema flex" data-testid="admin-layout">
            {/* Sidebar */}
            <aside className="w-64 bg-white border-r border-[#E5E0D8] flex flex-col">
                <div className="px-6 py-6 border-b border-[#E5E0D8]">
                    <Logo size={36} />
                </div>
                <nav className="flex-1 p-3 space-y-0.5">
                    {navItems.map((item) => {
                        if (item.master && user?.role !== "master") return null;
                        const Icon = item.icon;
                        return (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.end}
                                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className={({ isActive }) =>
                                    `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors duration-200 ${
                                        isActive
                                            ? "bg-[#3E2723] text-white"
                                            : "text-[#3E2723] hover:bg-[#F3EFEA]"
                                    }`
                                }
                            >
                                <Icon className="w-4 h-4" strokeWidth={1.5} />
                                {item.label}
                            </NavLink>
                        );
                    })}
                </nav>
                <div className="p-3 border-t border-[#E5E0D8]">
                    <a href="/" target="_blank" rel="noreferrer"
                        data-testid="open-scanner-link"
                        className="flex items-center gap-2 px-3 py-2 text-sm text-bc-orange hover:bg-[#F3EFEA] rounded-sm">
                        <QrCode className="w-4 h-4" strokeWidth={1.5} />
                        Open Scanner
                    </a>
                    <div className="px-3 py-2 text-xs text-[#7A726D]">
                        <div className="truncate">{user?.email}</div>
                        <div className="capitalize">{user?.role}</div>
                    </div>
                    <button
                        data-testid="logout-button"
                        onClick={handleLogout}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#B24A45] hover:bg-[#F3EFEA] rounded-sm"
                    >
                        <LogOut className="w-4 h-4" strokeWidth={1.5} /> Sign out
                    </button>
                </div>
            </aside>
            <main className="flex-1 overflow-auto">
                <Outlet />
            </main>
        </div>
    );
}
